import type { MatchFacts } from './history.js'
import { MARKETS, SUBJECT_MARKETS, sameOutcome, type MarketGroup, type MarketKey, type Outcome, type Resolution } from './markets.js'
import type { MarketRules, MatchParams } from './params.js'
import { priceOutcome } from './price.js'
import { hashSeed, simulateMatch } from './simulate.js'

export type QuotedOutcome = Outcome & { odds: number | null; probability: number; resolution: Resolution }
export type MarketStatus = 'open' | 'closed' | 'decided'
/** I gruppi della pagina partita, più gli antepost (tab Antepost). */
export type QuotedGroup = MarketGroup | 'outrights'
export type QuotedMarket = { key: MarketKey; group: QuotedGroup; status: MarketStatus; outcomes: QuotedOutcome[] }
export type QuoteInput = {
  params: MatchParams; facts: MatchFacts; cups: number; rules: MarketRules; margin: number; seed: number; runs?: number
  /** Partita in corso: alcuni mercati si giocano solo prima dell'inizio. */
  live?: boolean
}

/**
 * Tutti i mercati di una partita non conclusa: una simulazione, poi per ogni esito la regola del catalogo
 * (deciso dal vivo?) e il prezzo. Un esito aperto senza prezzo non si offre e sparisce; in un mercato chiuso
 * gli esiti restano, senza quota, per mostrarne lo stato.
 */
export function quoteMatch({ params, facts, cups, rules, margin, seed, runs, live = false }: QuoteInput): QuotedMarket[] {
  const sim = simulateMatch(params, facts, cups, { seed, runs })
  const ctx = { facts, cups, completed: false, live }
  // Fase del pareggio (una squadra senza bicchieri, tiro da fare): tutto chiuso, la partita si decide in un tiro.
  // Se il pareggio va a segno il bicchiere torna in gioco, `taken` scende sotto `cups` e il banco riapre.
  const raceOver = facts.takenByHome >= cups || facts.takenByAway >= cups
  const quoted: QuotedMarket[] = []
  for (const def of MARKETS) {
    if (!def.available(rules, params)) continue
    const closed = raceOver || (def.closed?.(ctx) ?? false)
    const priced = def.outcomes(sim, ctx).flatMap((outcome): QuotedOutcome[] => {
      const resolution = def.resolve(ctx, outcome)
      if (resolution !== 'open') return [{ ...outcome, odds: null, probability: resolution === 'won' ? 1 : 0, resolution }]
      const chance = def.chance(sim, outcome)
      const live = 1 - chance.void
      const odds = closed ? null : priceOutcome(chance, margin)
      if (odds === null && !closed) return []
      return [{ ...outcome, odds, probability: live > 0 ? chance.win / live : 0, resolution }]
    })
    // Linee a coppie: un lato senza quota fa cadere anche l'altro.
    const outcomes = def.pairedLines ? priced.filter((o) => priced.some((p) => p.line === o.line && p.selection !== o.selection)) : priced
    if (outcomes.length === 0) continue
    const status = outcomes.every((o) => o.resolution !== 'open') ? 'decided' : closed ? 'closed' : 'open'
    quoted.push({ key: def.key, group: def.group, status, outcomes })
  }
  return quoted
}

/** Quota corrente di un esito: null se ora non è offerto (mercato chiuso o deciso, esito deciso o senza prezzo). */
export function currentOdds(markets: readonly QuotedMarket[] | undefined, ref: Outcome): number | null {
  const market = markets?.find((m) => m.key === ref.market)
  if (!market || market.status !== 'open') return null
  const outcome = market.outcomes.find((o) => sameOutcome(o, ref))
  return outcome?.resolution === 'open' ? outcome.odds : null
}

/** Una partita da quotare per firma: niente seme, lo dà la firma. */
export type SignatureInput = Omit<QuoteInput, 'seed'>
export type CanonicalQuote = {
  /** Tutte e sole le variabili delle quote, senza id di partita, squadre e giocatori: firme uguali → quote identiche. */
  signature: string
  /** Da passare a `quoteMatch`: giocatori h0, h1…, a0, a1… (con le quote nello stesso ordine) e seme `hashSeed(signature)`. */
  input: QuoteInput
  /** Etichetta canonica → id vero, per `withPlayerIds`. */
  players: Map<string, string>
}

/** Chiavi in ordine alfabetico: due oggetti con gli stessi valori danno lo stesso testo, qualunque sia l'ordine d'inserimento. */
const sortedKeys = (_key: string, value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value) ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) : value

/**
 * Posizioni di una squadra per variabili, non per id: quota nella coppia decrescente, poi bicchieri già fatti in questa
 * partita decrescenti, poi id (a parità di tutto i due giocatori sono intercambiabili). Giocatori e quote restano appaiati.
 */
function canonicalOrder(players: readonly string[], shares: readonly number[], facts: MatchFacts) {
  const start = (id: string) => (facts.homePlayerCups[id] ?? 0) + (facts.awayPlayerCups[id] ?? 0)
  const order = players
    .map((id, i) => ({ id, share: shares[i] }))
    .sort((a, b) => b.share - a.share || start(b.id) - start(a.id) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  return { players: order.map((p) => p.id), shares: order.map((p) => p.share) }
}

/**
 * Forma canonica di una partita: i giocatori diventano posizioni (h0, h1… e a0, a1…) in un ordine che dipende solo
 * dalle loro variabili (`canonicalOrder`), le squadre H e A, e la firma è il testo di parametri, stato, bicchieri,
 * regole, margine e "in corso". Due partite con le stesse variabili hanno la stessa firma, quindi lo stesso seme e una sola simulazione condivisa.
 */
export function canonicalQuote({ params, facts, cups, rules, margin, runs, live = false }: SignatureInput): CanonicalQuote {
  const home = canonicalOrder(params.homePlayers, params.homeShares, facts)
  const away = canonicalOrder(params.awayPlayers, params.awayShares, facts)
  const labels = new Map<string, string>([
    ...home.players.map((id, i): [string, string] => [id, `h${i}`]),
    ...away.players.map((id, i): [string, string] => [id, `a${i}`]),
  ])
  // Chi ha segnato ma non è fra i giocatori quotati: etichetta per ordine di id, non entra in nessun esito offerto.
  const scorers = [...Object.keys(facts.homePlayerCups), ...Object.keys(facts.awayPlayerCups), ...(facts.firstScorer ? [facts.firstScorer] : [])]
  const unlisted = [...new Set(scorers)].filter((id) => !labels.has(id)).sort()
  unlisted.forEach((id, i) => labels.set(id, `x${i}`))
  const label = (id: string) => labels.get(id)!
  const relabel = (tally: Record<string, number>) => Object.fromEntries(Object.entries(tally).map(([id, n]) => [label(id), n]))
  const canonical: QuoteInput = {
    params: {
      ...params,
      homePlayers: home.players.map(label), homeShares: home.shares, awayPlayers: away.players.map(label), awayShares: away.shares,
    },
    facts: {
      ...facts,
      homeTeamId: 'H',
      awayTeamId: 'A',
      firstScorer: facts.firstScorer && label(facts.firstScorer),
      homePlayerCups: relabel(facts.homePlayerCups),
      awayPlayerCups: relabel(facts.awayPlayerCups),
    },
    cups, rules, margin, live, seed: 0, ...(runs !== undefined && { runs }),
  }
  // Fuori dalla firma la ripartizione per squadra degli airball della partita: le quote usano solo il totale.
  const { homeAirballs: _home, awayAirballs: _away, ...state } = canonical.facts
  const signature = JSON.stringify([canonical.params, state, cups, rules, margin, live, runs ?? null], sortedKeys)
  const players = new Map([...labels].filter(([, l]) => !l.startsWith('x')).map(([id, l]) => [l, id]))
  return { signature, input: { ...canonical, seed: hashSeed(signature) }, players }
}

/** Quote calcolate sulla forma canonica, riportate agli id veri: scelta e soggetto dei mercati giocatore. */
export function withPlayerIds(markets: readonly QuotedMarket[], players: ReadonlyMap<string, string>): QuotedMarket[] {
  const real = (id: string) => players.get(id) ?? id
  return markets.map((m) =>
    SUBJECT_MARKETS.has(m.key)
      ? { ...m, outcomes: m.outcomes.map((o) => ({ ...o, selection: real(o.selection), ...(o.subjectId !== undefined && { subjectId: real(o.subjectId) }) })) }
      : m,
  )
}
